import React, { FormEvent } from "react";
import { Package, Truck, Box, FileText, DollarSign, Upload, Plus, Info } from "lucide-react";
import { CARRIERS, shipmentUnitUoMOptions } from "src/helpers";
import { UpdatePurchaseOrderShipmentDetailsMapper } from "src/mappers";
import { matchCase } from "src/utils";
import { ValidationErrors } from "src/errors";
import { PurchaseOrder, UoM } from "src/types";
import {
  ShipmentUnitInputField,
  UpdatePurchaseOrderShipmentDetailsFormDataKey,
  UpdatePurchaseOrderShipmentDetailsFormData,
  UpdatePurchaseOrderShipmentDetailsFormAction
} from "src/types/forms/purchaseOrders";
import { useAuthorisation } from "src/context";
import { useScrollOnMessage } from "src/hooks";
import { ShipmentUnitInputTable } from "src/components/tables/inputTables";
import { SubmissionTooltip } from "src/components/tooltips";
import { MultiFileUpload, TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { OptionSelector } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";

interface UpdatePurchaseOrderShipmentDetailsFormProps {
  formData: UpdatePurchaseOrderShipmentDetailsFormData;
  purchaseOrderToUpdate?: PurchaseOrder;
  purchaseOrderOptions?: string[];
  validationErrors?: ValidationErrors<UpdatePurchaseOrderShipmentDetailsFormDataKey>;
  formLvlErrorMessage: string;
  isFormUpdated: boolean;
  shipmentUnitUoM: UoM;
  handleInputChange: (
    field: UpdatePurchaseOrderShipmentDetailsFormDataKey,
    value: UpdatePurchaseOrderShipmentDetailsFormData[keyof UpdatePurchaseOrderShipmentDetailsFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: UpdatePurchaseOrderShipmentDetailsFormAction) => void;
  setShipmentUnitUoM: React.Dispatch<React.SetStateAction<UoM>>;
  isDeliveryDetailsFieldsEnabled?: Boolean;
}

const UpdatePurchaseOrderShipmentDetailsForm: React.FC<UpdatePurchaseOrderShipmentDetailsFormProps> = ({
  formData,
  purchaseOrderToUpdate,
  purchaseOrderOptions,
  validationErrors,
  formLvlErrorMessage,
  isFormUpdated,
  shipmentUnitUoM,
  handleInputChange,
  handleSubmit,
  setAction,
  setShipmentUnitUoM,
  isDeliveryDetailsFieldsEnabled
}) => {
  const { userRole } = useAuthorisation();
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetShipmentUnits: React.Dispatch<React.SetStateAction<ShipmentUnitInputField[]>> = (
    updatedShipmentUnits
  ) => {
    handleInputChange("shipmentUnits", updatedShipmentUnits as ShipmentUnitInputField[]);
  };

  const handleAddShipmentUnits = () => {
    handleInputChange("shipmentUnits", [
      ...formData.shipmentUnits,
      UpdatePurchaseOrderShipmentDetailsMapper.initShipmentUnitInputField()
    ]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Update Shipment Details</h1>
            <p className="text-gray-300 mt-2 text-lg">Update tracking information and shipment details</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-id"
                  placeholder="Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("purchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.purchaseOrderId}
                  dropDownOptions={purchaseOrderOptions}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {purchaseOrderToUpdate && (
                <>
                  {isDeliveryDetailsFieldsEnabled && (
                    <>
                      {/* Tracking Information Section */}
                      <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                        <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                          <Truck className="w-4 h-4 mr-2" />
                          Tracking Information
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <TextInputBox
                              id="tracking-no"
                              placeholder="Tracking Number"
                              onChange={(value) => handleInputChange("trackingNo", value)}
                              value={formData.trackingNo}
                            />
                            <ValidationErrorMessage
                              message={validationErrors?.getErrorMessageByFormDataKey("trackingNo")}
                            />
                          </div>
                          {formData.trackingNo && (
                            <div>
                              <TextInputBox
                                id="carrier"
                                placeholder="Carrier"
                                onChange={(value) => handleInputChange("carrier", value)}
                                onBlur={() => handleInputChange("carrier", matchCase(formData.carrier, [...CARRIERS]))}
                                value={formData.carrier}
                                dropDownOptions={[...CARRIERS]}
                              />
                              <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                                <Info className="w-4 h-4 text-primary-500" />
                                <span>
                                  The carrier for the tracking number is required. If not in the dropdown, please type
                                  it manually.
                                </span>
                              </div>
                              <ValidationErrorMessage
                                message={validationErrors?.getErrorMessageByFormDataKey("carrier")}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Shipment Units Section */}
                      {formData.shipmentUnits.length > 0 && (
                        <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                          <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                            <Box className="w-4 h-4 mr-2" />
                            Shipment Units
                          </h3>
                          <ShipmentUnitInputTable
                            shipmentUnits={formData.shipmentUnits}
                            setShipmentUnits={handleSetShipmentUnits}
                            shipmentUnitUoM={shipmentUnitUoM}
                          />
                          <ValidationErrorMessage
                            message={validationErrors?.getErrorMessageByFormDataKey("shipmentUnits")}
                          />
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Unit of Measurement</label>
                            <OptionSelector
                              options={shipmentUnitUoMOptions}
                              onSelect={(option) => setShipmentUnitUoM((option as UoM) || "Metric")}
                              value={shipmentUnitUoM}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex justify-center">
                        <button
                          onClick={handleAddShipmentUnits}
                          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                          type="button"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Shipment Unit</span>
                        </button>
                      </div>
                    </>
                  )}

                  {/* Cost Information Section - Only for non-vendors */}
                  {userRole !== "VENDOR" && (
                    <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                      <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Cost Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <TextInputBox
                            id="shipment-cost"
                            placeholder="Shipping Cost (£)"
                            onChange={(value) => handleInputChange("shipmentCost", value)}
                            value={formData.shipmentCost}
                          />
                          <ValidationErrorMessage
                            message={validationErrors?.getErrorMessageByFormDataKey("shipmentCost")}
                          />
                        </div>
                        <div>
                          <TextInputBox
                            id="import-duty-cost"
                            placeholder="Import Duty Cost (£)"
                            onChange={(value) => handleInputChange("importDutyCost", value)}
                            value={formData.importDutyCost}
                          />
                          <ValidationErrorMessage
                            message={validationErrors?.getErrorMessageByFormDataKey("importDutyCost")}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Additional Information Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Additional Information
                    </h3>
                    <TextInputBoxLarge
                      id="shipment-note-body"
                      placeholder="Shipment Additional Info"
                      onChange={(value) => handleInputChange("additionalInfo", value)}
                      value={formData.additionalInfo}
                      maxLength={1000}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("additionalInfo")}
                    />
                  </div>

                  {/* Packing List Upload Section */}
                  {isDeliveryDetailsFieldsEnabled && (
                    <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                      <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                        <Upload className="w-4 h-4 mr-2" />
                        Packing List
                      </h3>
                      <MultiFileUpload
                        uploadText="Upload Packing List File(s)"
                        uploadedFiles={formData.packingListFiles}
                        onFileChange={(file) => handleInputChange("packingListFiles", file)}
                      />
                      <ValidationErrorMessage
                        message={validationErrors?.getErrorMessageByFormDataKey("packingListFiles")}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!isFormUpdated}
                  onClick={() => setAction(!purchaseOrderToUpdate ? "FETCH" : "UPDATE")}
                  type="submit"
                >
                  {!purchaseOrderToUpdate ? "Search" : "Update Shipment Details"}
                </button>

                {purchaseOrderToUpdate && !isFormUpdated && (
                  <SubmissionTooltip formState={isFormUpdated ? "INCOMPLETE" : "NO_CHANGE"} />
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { UpdatePurchaseOrderShipmentDetailsForm };
